import { Asset } from "expo-asset";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
    ActivityIndicator,
    Modal,
    Platform,
    Pressable,
    StyleSheet,
    Text,
    View
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { DocumentWebView } from "./DocumentWebView";

import { colors, fonts } from "@/lib/theme";
import { VisitDocumentCache } from "@/lib/visit-document-cache";

export interface DocumentRequest {
    cacheKey: string;
    title: string;
    contentType: string;
    loadUrl: () => Promise<string>;
}
const ViewerContext = createContext<(request: DocumentRequest) => void>(() => {});
export const useDocumentViewer = () => useContext(ViewerContext);

let viewerTemplate: Promise<string> | undefined;
function template() {
    viewerTemplate ??= (async () => {
        // Metro requires a static require to include this asset in native builds.
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const asset = Asset.fromModule(require("../../assets/document-viewer.html"));
        await asset.downloadAsync();
        const uri = asset.localUri ?? asset.uri;
        if (Platform.OS === "web") {
            return (await fetch(uri)).text();
        }
        return (await import("expo-file-system/legacy")).readAsStringAsync(uri);
    })().catch((error) => {
        viewerTemplate = undefined;
        throw error;
    });
    return viewerTemplate;
}
async function download(url: string, contentType: string) {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error("Could not load the document. Please try again.");
    }
    const blob = await response.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("Could not read the document."));
        reader.readAsDataURL(blob);
    });
    // Trust the server metadata rather than a storage service's generic MIME type.
    return { dataUrl: `data:${contentType};base64,${dataUrl.split(",")[1]}`, bytes: blob.size };
}

export function VisitDocumentViewerProvider({ children }: { children: ReactNode }) {
    const cache = useRef(new VisitDocumentCache());
    const generation = useRef(0);
    const [state, setState] = useState<{ title: string; html?: string; error?: string } | null>(
        null
    );
    useEffect(
        () => () => {
            generation.current++;
            cache.current.clear();
        },
        []
    );
    const close = () => {
        generation.current++;
        setState(null);
    };
    async function open(request: DocumentRequest) {
        const current = ++generation.current;
        setState({ title: request.title });
        try {
            const [file, html] = await Promise.all([
                cache.current.get(request.cacheKey, async () =>
                    download(await request.loadUrl(), request.contentType)
                ),
                template()
            ]);
            if (current !== generation.current) {
                return;
            }
            const input = JSON.stringify({
                contentType: request.contentType,
                dataUrl: file.dataUrl
            }).replace(/</g, "\\u003c");
            setState({
                title: request.title,
                html: html.replace(
                    "<!--DOCUMENT_DATA-->",
                    `<script>window.documentInput=${input}</script>`
                )
            });
        } catch (error) {
            if (current === generation.current) {
                setState({
                    title: request.title,
                    error: error instanceof Error ? error.message : "Could not open the document."
                });
            }
        }
    }
    return (
        <ViewerContext.Provider value={(request) => void open(request)}>
            {children}
            <Modal
                visible={state !== null}
                onRequestClose={close}
                animationType="slide"
                presentationStyle="fullScreen"
            >
                <SafeAreaView style={styles.screen}>
                    <View style={styles.header}>
                        <Text style={styles.title}>{state?.title}</Text>
                        <Pressable
                            onPress={close}
                            accessibilityRole="button"
                            accessibilityLabel="Close document"
                            style={styles.close}
                        >
                            <Text style={styles.closeText}>Close</Text>
                        </Pressable>
                    </View>
                    {state?.html ? (
                        <DocumentWebView html={state.html} title={state.title} />
                    ) : (
                        <View style={styles.loading}>
                            {state?.error ? (
                                <Text>{state.error}</Text>
                            ) : (
                                <ActivityIndicator color={colors.info600} />
                            )}
                        </View>
                    )}
                </SafeAreaView>
            </Modal>
        </ViewerContext.Provider>
    );
}
const styles = StyleSheet.create({
    screen: { flex: 1, backgroundColor: colors.surface },
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 8
    },
    title: { flex: 1, fontFamily: fonts.semiBold, fontSize: 15, color: colors.textPrimary },
    close: { minHeight: 44, justifyContent: "center", paddingHorizontal: 8 },
    closeText: { fontFamily: fonts.semiBold, color: colors.info600 },
    loading: { flex: 1, justifyContent: "center", alignItems: "center", padding: 24 }
});
